import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";

export type SortableIntegration = {
  id: string;
  provider: string;
  config?: Record<string, unknown> | null;
  instance_identifier?: string | null;
};

const WHATSAPP_PROVIDER_ORDER: Record<string, number> = {
  whatsapp_cloud: 0,
  whatsapp_uazapi: 1,
  whatsapp_evolution: 2,
};

function getConfigString(config: SortableIntegration["config"], key: string): string {
  const value = config?.[key];
  return typeof value === "string" ? value : "";
}

export function getWhatsAppProviderSortOrder(provider: string): number {
  return WHATSAPP_PROVIDER_ORDER[provider] ?? 99;
}

function compareAlphabetically(a: string, b: string): number {
  return a.localeCompare(b, "pt-BR", { sensitivity: "base" });
}

function getWhatsAppPhoneFallback(integration: SortableIntegration): string {
  const config = integration.config;
  return (
    getConfigString(config, "display_phone_number") ||
    getConfigString(config, "verified_name") ||
    getConfigString(config, "phone") ||
    getConfigString(config, "number") ||
    getConfigString(config, "phone_number") ||
    integration.instance_identifier ||
    ""
  );
}

function getWhatsAppAlphabeticalKey(integration: SortableIntegration): string {
  const label = whatsappIntegrationDisplayLabel(
    {
      instance_identifier: integration.instance_identifier,
      provider: integration.provider,
      config: {
        friendly_name: getConfigString(integration.config, "friendly_name") || null,
        instance_name:
          getConfigString(integration.config, "instance_name") ||
          getConfigString(integration.config, "instanceName") ||
          null,
        phone_number:
          getConfigString(integration.config, "phone_number") ||
          getConfigString(integration.config, "phone") ||
          null,
        display_phone_number: getConfigString(integration.config, "display_phone_number") || null,
        verified_name: getConfigString(integration.config, "verified_name") || null,
      },
    },
    "WhatsApp",
  );
  return label || getWhatsAppPhoneFallback(integration);
}

function getChannelAlphabeticalKey(
  integration: SortableIntegration,
  channel: "instagram" | "messenger",
): string {
  const config = integration.config;
  if (channel === "instagram") {
    const instagramUsername = getConfigString(config, "instagram_username");
    if (instagramUsername) return `@${instagramUsername}`;
    return (
      getConfigString(config, "page_name") ||
      getConfigString(config, "instagram_name") ||
      "Instagram"
    );
  }
  return getConfigString(config, "page_name") || "Messenger";
}

export function compareWhatsAppIntegrations(
  a: SortableIntegration,
  b: SortableIntegration,
): number {
  const providerDiff =
    getWhatsAppProviderSortOrder(a.provider) - getWhatsAppProviderSortOrder(b.provider);
  if (providerDiff !== 0) return providerDiff;

  const labelDiff = compareAlphabetically(
    getWhatsAppAlphabeticalKey(a),
    getWhatsAppAlphabeticalKey(b),
  );
  if (labelDiff !== 0) return labelDiff;

  return compareAlphabetically(getWhatsAppPhoneFallback(a), getWhatsAppPhoneFallback(b));
}

export function compareChannelIntegrations(
  a: SortableIntegration,
  b: SortableIntegration,
  channel: "instagram" | "messenger",
): number {
  const labelDiff = compareAlphabetically(
    getChannelAlphabeticalKey(a, channel),
    getChannelAlphabeticalKey(b, channel),
  );
  if (labelDiff !== 0) return labelDiff;
  return compareAlphabetically(a.id, b.id);
}

export function sortWhatsAppIntegrations<T extends SortableIntegration>(integrations: T[]): T[] {
  return [...integrations].sort(compareWhatsAppIntegrations);
}

export function sortInstagramIntegrations<T extends SortableIntegration>(integrations: T[]): T[] {
  return [...integrations].sort((a, b) => compareChannelIntegrations(a, b, "instagram"));
}

export function sortMessengerIntegrations<T extends SortableIntegration>(integrations: T[]): T[] {
  return [...integrations].sort((a, b) => compareChannelIntegrations(a, b, "messenger"));
}
